import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Loader2 } from "lucide-react";

import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../../components/ui/tabs";

import { useToast } from "../../components/hooks/use-toast";

import HazardDetails from "./HazardDetails";
import HazardCausesConsequences from "./HazardCausesConsequences";
import HazardMitigations from "./HazardMitigations";
import HazardSFAIRPStatement from "./HazardSFAIRPStatement";
import HazardAudit from "./HazardAudit";

import { calculateRiskRating } from "../../helpers/riskHelper";

import hazardService from "../../services/hazardService";
import analyticsService from "../../services/analyticsService";

const HazardEditorTabs = ({ project, riskMatrix, hazard, setHazard }) => {
  const [activeTab, setActiveTab] = useState("details");
  const [isUpdating, setIsUpdating] = useState(false);

  const { toast } = useToast();
  const navigate = useNavigate();

  const updateHazard = async () => {
    setIsUpdating(true);

    try {
      const editedHazard = {
        ...hazard,
        unmitigatedRisk: {
          ...hazard.unmitigatedRisk,
          rating: calculateRiskRating(
            hazard.unmitigatedRisk.probability,
            hazard.unmitigatedRisk.severity,
            riskMatrix
          ),
        },
        retainedRisk: {
          ...hazard.retainedRisk,
          rating: calculateRiskRating(
            hazard.retainedRisk.probability,
            hazard.retainedRisk.severity,
            riskMatrix
          ),
        },
      };

      await hazardService.updateHazard(project, editedHazard);
      
      // Track hazard update in analytics
      analyticsService.trackHazardUpdate(hazard.id, activeTab);
      
      // Track risk level changes if they've changed
      const originalHazard = hazard._original || {};
      
      if (originalHazard.unmitigatedRisk?.rating !== editedHazard.unmitigatedRisk.rating ||
          originalHazard.retainedRisk?.rating !== editedHazard.retainedRisk.rating) {
        
        analyticsService.trackRiskLevelChange(
          hazard.id,
          `${originalHazard.unmitigatedRisk?.rating || 'unknown'}-${originalHazard.retainedRisk?.rating || 'unknown'}`,
          `${editedHazard.unmitigatedRisk.rating}-${editedHazard.retainedRisk.rating}`
        );
      }

      navigate(-1);
    } catch (error) {
      console.error("Failed to update hazard.", error);
      
      // Track error in analytics
      analyticsService.trackError('hazard_update_error', error.message, 'updateHazard');

      toast({
        variant: "destructive",
        title: "Failed to update hazard.",
        description: error.message,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle>Edit Hazard</CardTitle>
      </CardHeader>

      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Tabs List */}
          <TabsList className="w-full flex justify-center">
            <TabsTrigger value="details" className="flex-grow text-center">
              Details
            </TabsTrigger>

            <TabsTrigger
              value="causes-consequences"
              className="flex-grow text-center"
            >
              Causes & Consequences
            </TabsTrigger>

            <TabsTrigger value="mitigations" className="flex-grow text-center">
              Mitigations
            </TabsTrigger>

            <TabsTrigger value="sfairp" className="flex-grow text-center">
              SFAIRP Statement
            </TabsTrigger>

            <TabsTrigger value="audit" className="flex-grow text-center">
              Audit
            </TabsTrigger>
          </TabsList>

          {/* Details Tab */}
          <TabsContent value="details">
            <HazardDetails
              project={project}
              hazard={hazard}
              setHazard={setHazard}
            />
          </TabsContent>

          {/* Causes & Consequences Tab */}
          <TabsContent value="causes-consequences">
            <HazardCausesConsequences
              project={project}
              riskMatrix={riskMatrix}
              hazard={hazard}
              setHazard={setHazard}
            />
          </TabsContent>

          {/* Mitigations Tab */}
          <TabsContent value="mitigations">
            <HazardMitigations
              project={project}
              riskMatrix={riskMatrix}
              hazard={hazard}
              setHazard={setHazard}
            />
          </TabsContent>

          {/* SFAIRP Statement Tab */}
          <TabsContent value="sfairp">
            <HazardSFAIRPStatement
              project={project}
              hazard={hazard}
              setHazard={setHazard}
            />
          </TabsContent>

          {/* Audit Tab */}
          <TabsContent value="audit">
            <HazardAudit />
          </TabsContent>
        </Tabs>
      </CardContent>

      <CardFooter className="mx-auto mt-4">
        <div className="w-full">
          <div className="flex justify-end">
            <Button
              className="min-w-[120px]"
              onClick={updateHazard}
              disabled={isUpdating}
            >
              {isUpdating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  <span>Updating...</span>
                </>
              ) : (
                <>
                  <span>Update</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
};

export default HazardEditorTabs;
